import React, { useEffect, useState } from 'react';
import './OurWork.css';
import { TopNav, SiteFooter } from './SiteChrome';
import { useLanguage } from './LanguageContext';
import { portfolioItems } from './portfolioData';

const PortfolioCard = ({ item, index, content }) => {
  const isOnline = item.status.toLowerCase() === 'online';
  
  return (
    <div className={`portfolio-card reveal delay-${(index % 4 + 1) * 100}`}>
      <div className="portfolio-image-wrapper">
        <img 
          src={item.image} 
          alt={item.name} 
          className="portfolio-img"
          loading="lazy"
        />
        <div className="portfolio-overlay">
          <a 
            href={item.url} 
            target="_blank" 
            rel="noopener noreferrer"
            className="btn-primary group"
            style={{ textDecoration: 'none' }}
          >
            {content.works.visitSite}
            <span className="material-symbols-outlined icon">open_in_new</span>
          </a>
        </div>
      </div>
      
      <div className="portfolio-info">
        <div className="portfolio-header">
          <h3 className="portfolio-name">
            {item.icon && (
              <img 
                src={item.icon} 
                alt="" 
                className="project-favicon"
                loading="lazy"
              />
            )}
            {item.name}
          </h3>
          <span className={`status-badge ${isOnline ? 'status-online' : 'status-offline'}`}>
            <span className="status-dot"></span>
            {isOnline ? content.works.statusOnline : content.works.statusOffline}
          </span>
        </div>
        
        {item.tags && item.tags.length > 0 && (
          <div className="portfolio-tags">
            {item.tags.map(tag => (
              <span key={tag} className="portfolio-tag">{tag}</span>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const OurWork = () => {
  const { content } = useLanguage();
  const workItems = portfolioItems.filter(item => !item.isClientOnly);
  const shouldPaginate = workItems.length > 8;
  const initialCount = shouldPaginate ? 4 : workItems.length;
  const [visibleCount, setVisibleCount] = useState(initialCount);
  const pageSize = 4;

  useEffect(() => {
    // Scroll reveal animation
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add('revealed');
          }
        });
      },
      { threshold: 0.1 }
    );
    
    document.querySelectorAll('.reveal').forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, [visibleCount]);

  const visibleItems = workItems.slice(0, visibleCount);
  const canLoadMore = shouldPaginate && visibleCount < workItems.length;
  const clientLogos = portfolioItems.filter((item) => item.icon);

  return (
    <div className="work-page">
      <TopNav />

      <main className="work-main">
        <div className="work-particle-bg" aria-hidden="true"></div>

        {/* Hero Section */}
        <section className="work-hero animate-fade-up">
          <span className="section-label">{content.works.eyebrow}</span>
          <h1 className="work-title">
            {content.works.title} <br />
            <span className="cosmic-gradient-text">{content.works.titleHighlight}</span>
          </h1>
          <p className="work-subtitle">{content.works.subtitle}</p>
        </section>

        {/* Portfolio Grid */}
        <section className="work-grid-section">
          <div className="portfolio-grid">
            {visibleItems.map((item, index) => (
              <PortfolioCard 
                key={item.id} 
                item={item} 
                index={index} 
                content={content} 
              />
            ))}
          </div>

          {canLoadMore && (
            <div className="portfolio-actions">
              <button
                type="button"
                className="load-more-button"
                onClick={() => {
                  setVisibleCount((prev) => Math.min(prev + pageSize, workItems.length));
                }}
              >
                {content.works.loadMore}
              </button>
            </div>
          )}
        </section>

        <section className="clients-section" aria-label={content.works.clientsTitle}>
          <div className="clients-inner">
            <h2 className="clients-title reveal">{content.works.clientsTitle}</h2>
            <div className="clients-grid">
              {clientLogos.map((item, index) => (
                <img
                  key={item.id}
                  src={item.icon}
                  alt=""
                  className={`client-logo reveal delay-${(index % 5 + 1) * 100}`}
                  loading="lazy"
                />
              ))}
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
};

export default OurWork;
